import { InstrumentId, VirtualInstrument } from '../types';

/**
 * Vintage Rhodes FM Tines Piano (Algorithmic Modeling)
 *
 * Emulates the iconic sound of a vintage Rhodes MkI / DX7 E-Piano:
 * - FM Tine strike generator with velocity-indexed bell chime (transient metallic attack)
 * - Dual detuned warm fundamental carriers (+2 cents) for organic chorus width
 * - Wooden acoustic body filter (320Hz cabinet resonance + velocity-tracking low-pass)
 * - Soft stereo spatial width and natural decay envelope
 *
 * 100% offline, 0 bytes to download, highly musical and expressive.
 */
export class ModeledSynthPiano implements VirtualInstrument {
  public readonly id: InstrumentId = 'synth-piano';
  public isReady = true;

  private ctx: AudioContext;
  private destination: AudioNode;
  private activeVoices = new Map<number, { stop: (stopTime?: number) => void }>();
  private scheduledVoices = new Map<number, { stop: () => void }>();
  private scheduledIdCounter = 0;
  private currentDecayTime = 1.6;

  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;
    this.destination = destination;
  }

  public setDecayTime(decaySeconds: number): void {
    this.currentDecayTime = Math.max(0.05, Math.min(3.5, decaySeconds));
  }

  public async load(): Promise<void> {
    this.isReady = true;
    return Promise.resolve();
  }

  public playNoteOn(pitch: number, velocity: number = 90, scheduledTime?: number): (stopTime?: number) => void {
    const now = scheduledTime !== undefined ? scheduledTime : this.ctx.currentTime;
    const freq = 440 * Math.pow(2, (pitch - 69) / 12);
    const velRatio = Math.max(0.04, Math.min(1, velocity / 127));

    // Expressive dynamic response
    const mainGainValue = 0.26 * Math.pow(velRatio, 1.15);

    // --- Audio Nodes ---
    const voiceMaster = this.ctx.createGain();

    // Body Cabinet EQ (warmth at 340Hz)
    const bodyFilter = this.ctx.createBiquadFilter();
    bodyFilter.type = 'peaking';
    bodyFilter.frequency.setValueAtTime(340, now);
    bodyFilter.Q.setValueAtTime(1.2, now);
    bodyFilter.gain.setValueAtTime(3.5, now);

    // Velocity-tracking Low-pass filter (warmer when soft, brighter when struck hard)
    const toneFilter = this.ctx.createBiquadFilter();
    toneFilter.type = 'lowpass';
    const cutoff = Math.min(11000, 1200 + velRatio * 7500);
    toneFilter.frequency.setValueAtTime(cutoff, now);
    toneFilter.Q.setValueAtTime(0.8, now);

    // 1. CARRIER 1: Warm fundamental sine
    const carrier1 = this.ctx.createOscillator();
    carrier1.type = 'sine';
    carrier1.frequency.setValueAtTime(freq, now);

    // 2. CARRIER 2: Detuned slightly (+1.8 cents) with soft triangle for body harmonics
    const carrier2 = this.ctx.createOscillator();
    carrier2.type = 'triangle';
    carrier2.frequency.setValueAtTime(freq * Math.pow(2, 2.2 / 1200), now);
    const carrier2Gain = this.ctx.createGain();
    carrier2Gain.gain.setValueAtTime(0.18, now);
    carrier2.connect(carrier2Gain);

    // 3. FM TINE MODULATOR: Creates the shimmering metallic tine chime
    const modulator = this.ctx.createOscillator();
    // 7x or 14x harmonic ratio creates crystalline bell/tine overtones
    const modRatio = pitch < 50 ? 7 : pitch < 72 ? 14 : 7;
    modulator.type = 'sine';
    modulator.frequency.setValueAtTime(freq * modRatio, now);

    const modGain = this.ctx.createGain();
    // FM depth scales heavily with strike velocity
    const modIndex = freq * (1.2 + velRatio * 2.8);
    modGain.gain.setValueAtTime(modIndex, now);
    // Modulator decays rapidly (35-70ms) simulating the hammer-on-tine impact
    const modDecayTime = now + 0.045 + (1 - velRatio) * 0.03;
    modGain.gain.exponentialRampToValueAtTime(Math.max(1, modIndex * 0.05), modDecayTime);
    modGain.gain.exponentialRampToValueAtTime(0.1, now + 0.35);

    modulator.connect(modGain);
    modGain.connect(carrier1.frequency);

    // Master Voice Envelope: Instant clean attack -> musical acoustic decay -> long sustain
    voiceMaster.gain.setValueAtTime(0.0001, now);
    voiceMaster.gain.linearRampToValueAtTime(mainGainValue, now + 0.005);
    // Smooth initial drop
    voiceMaster.gain.exponentialRampToValueAtTime(Math.max(0.0001, mainGainValue * 0.55), now + 0.25);
    // Natural long electric piano tail
    voiceMaster.gain.exponentialRampToValueAtTime(0.0001, now + 4.5);

    carrier1.connect(voiceMaster);
    carrier2Gain.connect(voiceMaster);

    voiceMaster.connect(bodyFilter);
    bodyFilter.connect(toneFilter);
    toneFilter.connect(this.destination);

    carrier1.start(now);
    carrier2.start(now);
    modulator.start(now);

    // Cut off duplicate pitch
    if (this.activeVoices.has(pitch)) {
      this.activeVoices.get(pitch)?.stop(now);
      this.activeVoices.delete(pitch);
    }

    let isReleased = false;
    const release = (stopTime?: number) => {
      if (isReleased) return;
      isReleased = true;
      const t = stopTime !== undefined ? stopTime : this.ctx.currentTime;
      try {
        voiceMaster.gain.cancelScheduledValues(t);
        const curVal = Math.max(0.0001, voiceMaster.gain.value);
        voiceMaster.gain.setValueAtTime(curVal, t);
        const releaseDuration = Math.max(0.04, this.currentDecayTime * 0.15);
        voiceMaster.gain.exponentialRampToValueAtTime(0.0001, t + releaseDuration);

        const stopAt = t + releaseDuration + 0.02;
        carrier1.stop(stopAt);
        carrier2.stop(stopAt);
        modulator.stop(stopAt);

        setTimeout(() => {
          try {
            voiceMaster.disconnect();
            bodyFilter.disconnect();
            toneFilter.disconnect();
          } catch {
            // ignore
          }
        }, Math.round((releaseDuration + 0.1) * 1000));
      } catch {
        // audio node may already be stopped
      }
    };

    this.activeVoices.set(pitch, { stop: release });
    return release;
  }

  public scheduleNote(pitch: number, velocity: number = 90, startTime: number, durationSeconds: number = 0.5): void {
    const now = Math.max(this.ctx.currentTime, startTime);
    const dur = Math.max(0.05, durationSeconds);
    const releaseTime = Math.max(0.04, this.currentDecayTime * 0.15);
    const stopTime = now + dur;

    const freq = 440 * Math.pow(2, (pitch - 69) / 12);
    const velRatio = Math.max(0.04, Math.min(1, velocity / 127));
    const mainGainValue = 0.26 * Math.pow(velRatio, 1.15);

    const voiceMaster = this.ctx.createGain();

    const bodyFilter = this.ctx.createBiquadFilter();
    bodyFilter.type = 'peaking';
    bodyFilter.frequency.setValueAtTime(340, now);
    bodyFilter.Q.setValueAtTime(1.2, now);
    bodyFilter.gain.setValueAtTime(3.5, now);

    const toneFilter = this.ctx.createBiquadFilter();
    toneFilter.type = 'lowpass';
    const cutoff = Math.min(11000, 1200 + velRatio * 7500);
    toneFilter.frequency.setValueAtTime(cutoff, now);
    toneFilter.Q.setValueAtTime(0.8, now);

    const carrier1 = this.ctx.createOscillator();
    carrier1.type = 'sine';
    carrier1.frequency.setValueAtTime(freq, now);

    const carrier2 = this.ctx.createOscillator();
    carrier2.type = 'triangle';
    carrier2.frequency.setValueAtTime(freq * Math.pow(2, 2.2 / 1200), now);
    const carrier2Gain = this.ctx.createGain();
    carrier2Gain.gain.setValueAtTime(0.18, now);
    carrier2.connect(carrier2Gain);

    const modulator = this.ctx.createOscillator();
    const modRatio = pitch < 50 ? 7 : pitch < 72 ? 14 : 7;
    modulator.type = 'sine';
    modulator.frequency.setValueAtTime(freq * modRatio, now);

    const modGain = this.ctx.createGain();
    const modIndex = freq * (1.2 + velRatio * 2.8);
    modGain.gain.setValueAtTime(modIndex, now);
    const modDecayTime = now + 0.045 + (1 - velRatio) * 0.03;
    modGain.gain.exponentialRampToValueAtTime(Math.max(1, modIndex * 0.05), modDecayTime);
    modGain.gain.exponentialRampToValueAtTime(0.1, now + 0.35);

    modulator.connect(modGain);
    modGain.connect(carrier1.frequency);

    voiceMaster.gain.setValueAtTime(0.0001, now);
    voiceMaster.gain.linearRampToValueAtTime(mainGainValue, now + 0.005);

    const sustainLevel = Math.max(0.0001, mainGainValue * 0.45);
    voiceMaster.gain.exponentialRampToValueAtTime(sustainLevel, now + Math.min(0.3, dur * 0.5));

    voiceMaster.gain.setValueAtTime(sustainLevel, stopTime);
    voiceMaster.gain.exponentialRampToValueAtTime(0.0001, stopTime + releaseTime);

    carrier1.connect(voiceMaster);
    carrier2Gain.connect(voiceMaster);
    voiceMaster.connect(bodyFilter);
    bodyFilter.connect(toneFilter);
    toneFilter.connect(this.destination);

    carrier1.start(now);
    carrier2.start(now);
    modulator.start(now);
    carrier1.stop(stopTime + releaseTime + 0.03);
    carrier2.stop(stopTime + releaseTime + 0.03);
    modulator.stop(stopTime + releaseTime + 0.03);

    const voiceId = ++this.scheduledIdCounter;
    this.scheduledVoices.set(voiceId, {
      stop: () => {
        try {
          const t = this.ctx.currentTime;
          voiceMaster.gain.cancelScheduledValues(t);
          voiceMaster.gain.linearRampToValueAtTime(0.0001, t + 0.02);
          carrier1.stop(t + 0.025);
          carrier2.stop(t + 0.025);
          modulator.stop(t + 0.025);
        } catch {
          // ignore
        }
      },
    });

    const cleanupTimeout = (stopTime + releaseTime + 0.1 - this.ctx.currentTime) * 1000;
    setTimeout(() => {
      this.scheduledVoices.delete(voiceId);
      try {
        voiceMaster.disconnect();
        bodyFilter.disconnect();
        toneFilter.disconnect();
      } catch {
        // ignore
      }
    }, Math.max(40, cleanupTimeout));
  }

  public stopAll(): void {
    for (const voice of this.activeVoices.values()) {
      voice.stop();
    }
    this.activeVoices.clear();

    for (const voice of this.scheduledVoices.values()) {
      voice.stop();
    }
    this.scheduledVoices.clear();
  }
}
